import { Navigate, Outlet, useLocation } from "react-router";

import { IdleSessionGuard } from "@/components/shell/IdleSessionGuard";
import { ThemeSync } from "@/components/shell/ThemeSync";
import { ApiError } from "@/lib/api";
import { useCurrentActor } from "@/lib/current-actor";

/**
 * Gap-fill (frontend auth UI), judgement call 2 — the auth redirect gate.
 * Rendered as a layout route wrapping `AppShell`'s own route in `main.tsx`,
 * so every one of the nine nav destinations and every record page renders
 * behind this check without any of them changing themselves.
 *
 * Three states, matching `useCurrentActor()`'s query exactly:
 *
 * - **Pending**: renders nothing. Never the real `AppShell`/`<Outlet/>`
 *   children — that would be exactly the flash of protected content or nav
 *   chrome the AC forbids (VC-GF1-01). A blank frame for one network
 *   round-trip is the safer failure mode than a flash of real data.
 * - **A resolved 401** (`error.status === 401`, checked specifically rather
 *   than a bare `isError` — `ApiError` already carries `.status`, so a
 *   genuine network/server error is never silently treated as "go sign
 *   in"): redirects to `/sign-in?redirect=<current path + search>`, so a
 *   successful sign-in can return the visitor to exactly where they were
 *   headed (VC-GF1-02). `replace` so the protected URL never lands in
 *   history as a page the visitor could "back" into.
 * - **Any other error** (a real network failure, a 500, anything that is
 *   not "you are not signed in"): still never renders the real children,
 *   but does not assume signed-out either — a transient failure while
 *   genuinely signed in should not bounce someone to the sign-in screen. A
 *   minimal neutral message renders instead.
 * - **Success**: renders `<Outlet/>`, which is `AppShell` and everything
 *   under it, normally — alongside `IdleSessionGuard` (Story 15.7), mounted
 *   once here so every screen behind this gate is covered by the idle timer
 *   uniformly, without any route opting in.
 */
export function RequireAuth() {
  const { isPending, isError, error } = useCurrentActor();
  const location = useLocation();

  if (isPending) {
    return null;
  }

  if (isError) {
    if (error instanceof ApiError && error.status === 401) {
      const target = `${location.pathname}${location.search}`;
      return <Navigate to={`/sign-in?redirect=${encodeURIComponent(target)}`} replace />;
    }
    return (
      <div className="flex min-h-screen items-center justify-center bg-canvas px-4 text-center">
        <p className="text-body text-ink-secondary">
          Something went wrong loading your account. Please reload the page.
        </p>
      </div>
    );
  }

  return (
    <>
      {/* Story 15.7 — mounted once, here, so every screen behind the auth
       * gate is covered by the idle timer uniformly (judgement call 3),
       * the same "one place above every route" shape `Toaster`/
       * `SearchOverlay` already use in `AppShell.tsx`. */}
      <IdleSessionGuard />
      {/* Story 18.2, judgement call 3 — the same "one place above every
       * route" shape as `IdleSessionGuard` above. */}
      <ThemeSync />
      <Outlet />
    </>
  );
}
